import { supabase } from '@/integrations/supabase/client';

interface ZoomMeetingData {
  id: string;
  join_url: string;
  start_url: string;
  topic: string;
  start_time: string;
  duration: number;
  timezone: string;
}

/**
 * Create a Zoom meeting for a booking
 */
export const createZoomMeeting = async (params: {
  userId: string;
  meetingLinkId: string;
  bookingId: string;
  startTime: string;
  duration: number;
  topic: string;
  timezone?: string;
}): Promise<{ success: boolean; meeting?: ZoomMeetingData; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('zoom-create-meeting', {
      body: {
        user_id: params.userId,
        meeting_link_id: params.meetingLinkId,
        booking_id: params.bookingId,
        start_time: params.startTime,
        duration: params.duration,
        topic: params.topic,
        timezone: params.timezone || 'UTC'
      }
    });

    if (error) {
      console.error('Error creating Zoom meeting:', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Failed to create Zoom meeting' };
    }

    return { success: true, meeting: data.meeting };
  } catch (error: any) {
    console.error('Error creating Zoom meeting:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a Zoom meeting (reschedule)
 */
export const updateZoomMeeting = async (params: {
  userId: string;
  meetingId: string;
  startTime: string;
  duration: number;
  topic: string;
  timezone?: string;
}): Promise<{ success: boolean; meeting?: ZoomMeetingData; error?: string }> => {
  try {
    // For now, we'll create a new meeting and delete the old one
    // In a production system, you might want to use Zoom's update meeting API
    const { data, error } = await supabase.functions.invoke('zoom-update-meeting', {
      body: {
        user_id: params.userId,
        meeting_id: params.meetingId,
        start_time: params.startTime,
        duration: params.duration,
        topic: params.topic,
        timezone: params.timezone || 'UTC'
      }
    });

    if (error) {
      console.error('Error updating Zoom meeting:', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Failed to update Zoom meeting' };
    }

    return { success: true, meeting: data.meeting };
  } catch (error: any) {
    console.error('Error updating Zoom meeting:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a Zoom meeting
 */
export const deleteZoomMeeting = async (params: {
  userId: string;
  meetingId: string;
}): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('zoom-delete-meeting', {
      body: {
        user_id: params.userId,
        meeting_id: params.meetingId
      }
    });

    if (error) {
      console.error('Error deleting Zoom meeting:', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Failed to delete Zoom meeting' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting Zoom meeting:', error);
    return { success: false, error: error.message };
  }
};
